import { useQuery } from "@tanstack/react-query";
import { getApiErrorMessage } from "../services/apiErrors";
import { readStoryEngineSql, type StoryEngineSqlApi } from "../services/storyCanvas/storyEngineSqlApi";
import { requestStoryRender } from "../services/storyCanvas/storyRendersApi";

export const ENGINE_SQL_LOAD_FALLBACK = "Couldn't load the engine SQL.";
const POLL_MS = 1000;

export interface UseStoryItemEngineSqlResult {
  state: "idle" | "loading" | "error" | "ready";
  data: StoryEngineSqlApi | null;
  errorMessage: string | null;
}

/**
 * The SQL the engine ran for a chart: starts a render of the item for this reader, then reads the
 * engine SQL of that render until it stops saying `pending`. Nothing is fetched until `enabled`.
 */
export const useStoryItemEngineSql = (
  storyId: string,
  itemId: string | null,
  enabled: boolean,
  pollMs: number = POLL_MS,
): UseStoryItemEngineSqlResult => {
  const active = enabled && Boolean(storyId) && Boolean(itemId);
  const render = useQuery({
    queryKey: ["storyItemEngineSql", storyId, itemId, "render"],
    queryFn: () => requestStoryRender(storyId, [itemId as string]),
    enabled: active,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const renderId = render.data?.render_id ?? null;
  const sql = useQuery({
    queryKey: ["storyItemEngineSql", storyId, itemId, "sql", renderId],
    queryFn: () => readStoryEngineSql(storyId, renderId as string, itemId as string),
    enabled: active && renderId !== null,
    retry: false,
    refetchInterval: (query) => (query.state.data?.status === "pending" ? pollMs : false),
  });
  if (!active) return { state: "idle", data: null, errorMessage: null };
  const error = render.error ?? sql.error;
  if (error) return { state: "error", data: null, errorMessage: getApiErrorMessage(error, ENGINE_SQL_LOAD_FALLBACK) };
  if (sql.data && sql.data.status !== "pending") return { state: "ready", data: sql.data, errorMessage: null };
  return { state: "loading", data: null, errorMessage: null };
};
